/**
 * System-reserved lot names that are automatically generated and cannot be manually assigned by users.
 *
 * - UNSPECIFIED: Automatically created for Direct Ship (DSO) and Blind Ship orders
 * - UNASSIGNED: Automatically created for Stock orders when lots haven't been assigned yet
 *
 * These lot names are critical to the automatic lot allocation system and should not be
 * manually assignable to prevent data corruption and inventory tracking issues.
 */
export const RESERVED_LOT_NAMES = ["UNSPECIFIED", "UNASSIGNED"] as const;

/**
 * Helper function to check if a lot name is system-reserved (case-insensitive).
 *
 * @param lotName - The lot name to check
 * @returns true if the lot name is reserved, false otherwise
 *
 * @example
 * isReservedLotName("UNSPECIFIED") // true
 * isReservedLotName("unspecified") // true
 * isReservedLotName("UNSPECIFIED-2") // false (partial match allowed)
 * isReservedLotName("LOT-123") // false
 */
export function isReservedLotName(lotName: string): boolean {
  const upperLotName = lotName.toUpperCase();
  return RESERVED_LOT_NAMES.some(reserved => upperLotName === reserved);
}

/**
 * True when lot number indicates unassigned state (needs user assignment).
 *
 * Unassigned lots use the plain `UNASSIGNED` sentinel (case-insensitive).
 *
 * Note: the legacy VWP `UNASSIGNED-{packageId}` prefix is intentionally NOT
 * recognized here. That format was tied to the now-removed Package model and
 * has zero production rows; reintroducing prefix matching would silently
 * resurrect the dead VWP code path. Keep the match strictly equal.
 */
export function isUnassignedLotNumber(lotNumber: string): boolean {
  return lotNumber.toUpperCase() === "UNASSIGNED";
}

/**
 * User-friendly error message for when a user tries to use a reserved lot name.
 */
export const RESERVED_LOT_NAMES_ERROR_MESSAGE =
  "'UNSPECIFIED' and 'UNASSIGNED' are system-reserved lot names and cannot be manually assigned.";
